/* eslint-disable no-param-reassign */
import { add, remove, toggle } from "shared/lib/arrayUtils"

import { PayloadAction } from "@reduxjs/toolkit"

import { ProjectReducerState } from "./types"

function initializeTable(projectID: string, state: ProjectReducerState) {
  if (!state[projectID]) {
    state[projectID] = {
      isDetailsViewOpen: false,
      collapsedRoomIDs: [],
      expandedProductPositionIDs: [],
      selectedElementIDs: [],
      pendingElementIDs: [],
      sidebarIsEditing: false,
      sidebarActiveTab: null
    }
  }
}

function resetSidebarState(projectID: string, state: ProjectReducerState) {
  state[projectID].sidebarIsEditing = false
  state[projectID].sidebarActiveTab = null
}

export interface SelectElementOptions {
  preventUnselectOnReselect?: boolean
  openDetailsView?: boolean
}

export function selectElementHandler(
  state: ProjectReducerState,
  action: PayloadAction<
    {
      projectID: string
      itemID: string | string[]
      isMulti?: boolean
    } & SelectElementOptions
  >
) {
  const {
    itemID,
    projectID,
    isMulti,
    preventUnselectOnReselect,
    openDetailsView
  } = action.payload
  initializeTable(projectID, state)
  const itemIDs = Array.isArray(itemID) ? itemID : [itemID]
  const currentSelection = state[projectID].selectedElementIDs
  const isSingleItem = itemIDs.length === 1
  const wasOnlySelection = currentSelection.length === 1
  const isAlreadySelected =
    isSingleItem && wasOnlySelection && currentSelection.includes(itemIDs[0])

  if (isMulti) {
    itemIDs.forEach(id => {
      state[projectID].selectedElementIDs = add(
        id,
        state[projectID].selectedElementIDs
      )
    })
    resetSidebarState(projectID, state)
  } else if (
    isAlreadySelected &&
    !preventUnselectOnReselect &&
    state[projectID].isDetailsViewOpen
  ) {
    state[projectID].selectedElementIDs = []
    state[projectID].isDetailsViewOpen = false
    resetSidebarState(projectID, state)
  } else if (isAlreadySelected && !preventUnselectOnReselect) {
    state[projectID].isDetailsViewOpen = true
  } else if (isAlreadySelected) {
    // preventUnselectOnReselect: no-op — keep current state
  } else if (itemIDs.length === 0) {
    state[projectID].selectedElementIDs = itemIDs
    state[projectID].isDetailsViewOpen = false
    resetSidebarState(projectID, state)
  } else {
    state[projectID].selectedElementIDs = itemIDs
    if (openDetailsView !== false) {
      state[projectID].isDetailsViewOpen = true
    }
    resetSidebarState(projectID, state)
  }
}

export function unselectAllHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; itemID?: string | string[] }>
) {
  const { projectID, itemID } = action.payload
  initializeTable(projectID, state)
  if (!itemID) {
    state[projectID].selectedElementIDs = []
    resetSidebarState(projectID, state)
    return
  }
  const itemIDs = Array.isArray(itemID) ? itemID : [itemID]
  itemIDs.forEach(id => {
    state[projectID].selectedElementIDs = remove(
      id,
      state[projectID].selectedElementIDs
    )
  })
  resetSidebarState(projectID, state)
}

export function openMenuHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string }>
) {
  const { projectID } = action.payload
  initializeTable(projectID, state)
  state[projectID].isDetailsViewOpen = true
}

export function activateTabHandler(
  state: ProjectReducerState,
  action: PayloadAction<{
    projectID: string
    tabID: string | null
    keepSelection?: boolean
  }>
) {
  const { projectID, keepSelection } = action.payload
  initializeTable(projectID, state)

  if (!keepSelection) {
    state[projectID].selectedElementIDs = []
    resetSidebarState(projectID, state)
  }
}

export function closeMenuHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string }>
) {
  const { projectID } = action.payload
  initializeTable(projectID, state)
  state[projectID].isDetailsViewOpen = false
  resetSidebarState(projectID, state)
}

export function setElementPendingHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; itemID: string }>
) {
  const { itemID, projectID } = action.payload
  initializeTable(projectID, state)
  state[projectID].pendingElementIDs = add(
    itemID,
    state[projectID].pendingElementIDs
  )
}

export function unsetElementPendingHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; itemID: string }>
) {
  const { itemID, projectID } = action.payload
  initializeTable(projectID, state)
  state[projectID].pendingElementIDs = remove(
    itemID,
    state[projectID].pendingElementIDs
  )
}

export function collapseRoomHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; roomID: string }>
) {
  const { projectID, roomID } = action.payload
  initializeTable(projectID, state)
  state[projectID].collapsedRoomIDs = toggle(
    roomID,
    state[projectID].collapsedRoomIDs
  )
}

export function expandProductPositionHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; productPositionID: string }>
) {
  const { projectID, productPositionID } = action.payload
  initializeTable(projectID, state)
  state[projectID].expandedProductPositionIDs = toggle(
    productPositionID,
    state[projectID].expandedProductPositionIDs
  )
}

export function collapseAllHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; roomIDs: string[] }>
) {
  const { projectID, roomIDs } = action.payload
  initializeTable(projectID, state)
  state[projectID].collapsedRoomIDs = roomIDs
  state[projectID].expandedProductPositionIDs = []
}

export function expandAllHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; productPositionIDs: string[] }>
) {
  const { projectID, productPositionIDs } = action.payload
  initializeTable(projectID, state)
  state[projectID].collapsedRoomIDs = []
  state[projectID].expandedProductPositionIDs = productPositionIDs
}

export function enterSidebarEditingHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string }>
) {
  const { projectID } = action.payload
  initializeTable(projectID, state)
  state[projectID].sidebarIsEditing = true
}

export function exitSidebarEditingHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string }>
) {
  const { projectID } = action.payload
  initializeTable(projectID, state)
  state[projectID].sidebarIsEditing = false
}

export function setSidebarActiveTabHandler(
  state: ProjectReducerState,
  action: PayloadAction<{ projectID: string; tab: string | null }>
) {
  const { projectID, tab } = action.payload
  initializeTable(projectID, state)
  state[projectID].sidebarActiveTab = tab
}
